import { ref } from 'vue';
import {
  VERSION_CHECK_INTERVAL_MS, VERSION_CHECK_MIN_GAP_MS, checkForUpdate, isFormDirty, parseServerId,
  type UpdateDecision,
} from './versionCheck';

/**
 * Browser wiring for the self-update check (the rule itself is in versionCheck.ts).
 *
 * Started once from main.ts. Checks on start, whenever the app returns to the foreground, and every
 * VERSION_CHECK_INTERVAL_MS while it stays visible.
 */

/** True once a newer build is known and the app could not reload by itself — AppUpdateBanner shows it. */
export const updateAvailable = ref(false);

const RELOADED_FOR_KEY = 'tacita.update.reloadedFor';
/** How long a reload waits for the new service worker to take over before reloading anyway. */
const SW_ACTIVATION_TIMEOUT_MS = 10_000;

function readReloadedFor(): string | null {
  try { return sessionStorage.getItem(RELOADED_FOR_KEY); } catch { return null; }
}

function writeReloadedFor(id: string): void {
  try { sessionStorage.setItem(RELOADED_FOR_KEY, id); } catch { /* private mode: the loop guard is best-effort */ }
}

async function fetchServerId(): Promise<string | null> {
  // `no-store` + a throwaway query: neither the HTTP cache nor any intermediary may answer for the server.
  const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store', credentials: 'omit' });
  if (!res.ok) return null;
  // The dev server and nginx's SPA fallback answer unknown paths with index.html — not JSON, not an id.
  if (!(res.headers.get('content-type') ?? '').includes('json')) return null;
  return parseServerId(await res.json());
}

function waitForActivation(worker: ServiceWorker): Promise<void> {
  return new Promise(resolve => {
    if (worker.state === 'activated' || worker.state === 'redundant') { resolve(); return; }
    const timer = window.setTimeout(resolve, SW_ACTIVATION_TIMEOUT_MS);
    worker.addEventListener('statechange', () => {
      if (worker.state === 'activated' || worker.state === 'redundant') {
        window.clearTimeout(timer);
        resolve();
      }
    });
  });
}

/**
 * Reloads onto the new build. The service worker precaches index.html and serves it for navigations, so
 * a bare reload would boot the OLD shell again from the precache. First make the SW fetch its new
 * version (sw.js is served no-cache) and let it activate — it is built with skipWaiting + clientsClaim,
 * so it takes over on its own once installed — then reload.
 */
export async function reloadToNewBuild(): Promise<void> {
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    if (reg) {
      await reg.update();
      const incoming = reg.installing ?? reg.waiting;
      if (incoming) await waitForActivation(incoming);
    }
  } catch {
    // No SW, or the update check failed — a plain reload is still the best that can be done.
  }
  window.location.reload();
}

/**
 * The banner's button. The automatic path above can land on the OLD shell again — the worker may still serve its
 * precached index.html — and the next check then raised the banner straight back, so after the tap it looked
 * like it never went away (owner, 02.10). Here the banner goes at once and the precache is dropped first: the
 * worker falls back to the network for anything missing from its cache, and nginx serves index.html no-cache,
 * so the reload boots the deployed build. The registration itself stays — unregistering would drop the client's
 * push subscription.
 */
export async function reloadToNewBuildByHand(): Promise<void> {
  updateAvailable.value = false;
  if (lastServerId) {
    writeManualFor(lastServerId);
    writeReloadedFor(lastServerId);   // this reload is the one for that build — no automatic second one after it
  }
  try {
    if (typeof caches !== 'undefined') {
      await Promise.all((await caches.keys()).map(k => caches.delete(k)));
    }
  } catch { /* storage refused — the reload below still tries */ }
  await reloadToNewBuild();
}

const MANUAL_FOR_KEY = 'tacita.update.manualFor';
/** The server build id last read by a check — so a hand reload can record which build it was for. */
let lastServerId: string | null = null;

function readManualFor(): string | null {
  try { return sessionStorage.getItem(MANUAL_FOR_KEY); } catch { return null; }
}

function writeManualFor(id: string): void {
  try { sessionStorage.setItem(MANUAL_FOR_KEY, id); } catch { /* private mode */ }
}

let lastCheckAt = 0;
let inFlight: Promise<UpdateDecision> | null = null;

let reloadWhenHidden = false;

/**
 * The periodic tick fires while the client is looking at the app; reloading then blinks the whole page in front
 * of them (owner, 02.10). That reload waits for the app to go to the background instead and happens unseen; if
 * the client comes back first, the foreground check reloads at once, while the screen is only being shown.
 */
function reloadOnceHidden(): void {
  if (reloadWhenHidden) return;
  reloadWhenHidden = true;
  const onHide = () => {
    if (document.visibilityState !== 'hidden') return;
    document.removeEventListener('visibilitychange', onHide);
    if (lastServerId) writeReloadedFor(lastServerId);   // marked only now: until then nothing was reloaded
    void reloadToNewBuild();
  };
  document.addEventListener('visibilitychange', onHide);
}

/**
 * One check, deduplicated and throttled; `force` skips the throttle (the periodic tick). `inUse` = the app is
 * on screen and was not just brought back: a reload is deferred until it is hidden.
 */
export function runUpdateCheck(force = false, inUse = false): Promise<UpdateDecision> {
  if (inFlight) return inFlight;
  const now = Date.now();
  if (!force && now - lastCheckAt < VERSION_CHECK_MIN_GAP_MS) return Promise.resolve('unknown');
  lastCheckAt = now;
  inFlight = checkForUpdate({
    currentId: __APP_BUILD_ID__,
    fetchServerId: async () => (lastServerId = await fetchServerId()),
    isFormDirty: () => isFormDirty(document),
    reloadedFor: readReloadedFor,
    markReloadedFor: inUse ? () => {} : writeReloadedFor,
    reload: inUse ? reloadOnceHidden : reloadToNewBuild,
    // Once the client has updated by hand for this build, never raise the banner for it again in this session.
    showBanner: () => { if (readManualFor() !== lastServerId) updateAvailable.value = true; },
  }).finally(() => { inFlight = null; });
  return inFlight;
}

let started = false;

export function startAppUpdateChecks(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  const onForeground = () => {
    if (document.visibilityState === 'visible') void runUpdateCheck();
  };
  document.addEventListener('visibilitychange', onForeground);
  window.addEventListener('focus', onForeground);
  window.addEventListener('online', onForeground);
  window.setInterval(() => {
    if (document.visibilityState === 'visible') void runUpdateCheck(true, true);
  }, VERSION_CHECK_INTERVAL_MS);
  void runUpdateCheck(true);
}
